import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';

interface Card {
  id: number;
  image: string;
  isFlipped: boolean;
  isMatched: boolean;
}

interface MemoryGameProps {
  onStatsUpdate: (stats: { bestScore: number; gamesPlayed: number }) => void;
}

const MemoryGame: React.FC<MemoryGameProps> = ({ onStatsUpdate }) => {
  const [cards, setCards] = useState<Card[]>([]);
  const [flippedCards, setFlippedCards] = useState<number[]>([]);
  const [moves, setMoves] = useState(0);
  const [matches, setMatches] = useState(0);
  const [gameComplete, setGameComplete] = useState(false);
  const [gameStarted, setGameStarted] = useState(false);
  const [bestScore, setBestScore] = useState(0);

  // Couple photos for the memory game
  const couplePhotos = [
    '/couple-photo.jpg',
    '/gallery2.jpg',
    '/gallery3.jpg',
    '/gallery4.jpg',
    '/gallery6.jpg',
    '/IMG_8780.heic',
    '/priya-photo.jpg',
    '/us.mov' // This will show a placeholder
  ];

  // Initialize game
  const initializeGame = useCallback(() => {
    const gamePhotos = couplePhotos.slice(0, 6); // Use 6 photos for 12 cards
    const cardPairs = gamePhotos.map((photo, index) => [
      { id: index * 2, image: photo, isFlipped: false, isMatched: false },
      { id: index * 2 + 1, image: photo, isFlipped: false, isMatched: false }
    ]).flat();

    // Shuffle cards
    const shuffledCards = cardPairs.sort(() => Math.random() - 0.5);
    
    setCards(shuffledCards);
    setFlippedCards([]);
    setMoves(0);
    setMatches(0);
    setGameComplete(false);
    setGameStarted(true);
  }, [couplePhotos]);

  // Load best score from localStorage
  useEffect(() => {
    const savedBestScore = localStorage.getItem('memoryGameBestScore');
    if (savedBestScore) {
      setBestScore(parseInt(savedBestScore));
    }
  }, []);

  // Handle card click
  const handleCardClick = (cardId: number) => {
    if (flippedCards.length >= 2 || cards.find(c => c.id === cardId)?.isFlipped || cards.find(c => c.id === cardId)?.isMatched) {
      return;
    }

    const newFlippedCards = [...flippedCards, cardId];
    setFlippedCards(newFlippedCards);

    setCards(prevCards =>
      prevCards.map(card =>
        card.id === cardId ? { ...card, isFlipped: true } : card
      )
    );

    if (newFlippedCards.length === 2) {
      setMoves(prev => prev + 1);
      
      setTimeout(() => {
        const [firstId, secondId] = newFlippedCards;
        const firstCard = cards.find(c => c.id === firstId);
        const secondCard = cards.find(c => c.id === secondId);

        if (firstCard?.image === secondCard?.image) {
          // Match found
          setCards(prevCards =>
            prevCards.map(card =>
              card.id === firstId || card.id === secondId
                ? { ...card, isMatched: true, isFlipped: true }
                : card
            )
          );
          setMatches(prev => prev + 1);
        } else {
          // No match, flip cards back
          setCards(prevCards =>
            prevCards.map(card =>
              card.id === firstId || card.id === secondId
                ? { ...card, isFlipped: false }
                : card
            )
          );
        }
        setFlippedCards([]);
      }, 1000);
    }
  };

  // Check for game completion
  useEffect(() => {
    if (matches === 6 && !gameComplete) {
      setGameComplete(true);
      const newBestScore = bestScore === 0 || moves < bestScore ? moves : bestScore;
      setBestScore(newBestScore);
      localStorage.setItem('memoryGameBestScore', newBestScore.toString());
      
      // Update stats
      const currentStats = JSON.parse(localStorage.getItem('gameStats') || '{}');
      const memoryStats = currentStats.memoryGame || { bestScore: 0, gamesPlayed: 0 };
      onStatsUpdate({
        bestScore: newBestScore,
        gamesPlayed: memoryStats.gamesPlayed + 1
      });
    }
  }, [matches, gameComplete, moves, bestScore, onStatsUpdate]);

  const CardComponent: React.FC<{ card: Card; index: number }> = ({ card, index }) => (
    <motion.div
      key={card.id}
      className="aspect-square cursor-pointer"
      onClick={() => handleCardClick(card.id)}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
    >
      <div className="relative w-full h-full">
        <motion.div
          className="absolute inset-0 bg-gradient-to-br from-rose-400 to-pink-500 rounded-2xl shadow-lg flex items-center justify-center"
          animate={{ rotateY: card.isFlipped ? 180 : 0 }}
          transition={{ duration: 0.6 }}
          style={{ transformStyle: 'preserve-3d' }}
        >
          {/* Card Back */}
          <div className="absolute inset-0 flex items-center justify-center" style={{ backfaceVisibility: 'hidden' }}>
            <div className="text-6xl">💕</div>
          </div>
          
          {/* Card Front */}
          <div 
            className="absolute inset-0 rounded-2xl overflow-hidden" 
            style={{ 
              backfaceVisibility: 'hidden',
              transform: 'rotateY(180deg)'
            }}
          >
            <img
              src={card.image}
              alt={`Memory card ${index + 1}`}
              className="w-full h-full object-cover"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
                target.parentElement!.innerHTML = `
                  <div class="w-full h-full bg-gradient-to-br from-rose-300 to-pink-400 flex items-center justify-center text-4xl">
                    💖
                  </div>
                `;
              }}
            />
            {card.isMatched && (
              <div className="absolute inset-0 bg-green-500/80 flex items-center justify-center">
                <div className="text-6xl">✅</div>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </motion.div>
  );

  return (
    <div className="w-full max-w-4xl mx-auto">
      {/* Game Header */}
      <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 mb-8 shadow-xl">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h3 className="text-2xl font-bold text-slate-800 mb-2">Memory Match Game</h3>
            <p className="text-slate-600">Match the couple photos to win! Find all 6 pairs.</p>
          </div>
          
          <div className="flex flex-wrap gap-4 text-sm">
            <div className="bg-blue-100 px-4 py-2 rounded-full">
              <span className="font-medium">Moves: </span>
              <span className="font-bold text-blue-600">{moves}</span>
            </div>
            <div className="bg-green-100 px-4 py-2 rounded-full">
              <span className="font-medium">Matches: </span>
              <span className="font-bold text-green-600">{matches}/6</span>
            </div>
            <div className="bg-purple-100 px-4 py-2 rounded-full">
              <span className="font-medium">Best: </span>
              <span className="font-bold text-purple-600">{bestScore}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Game Board */}
      <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 shadow-xl">
        {!gameStarted ? (
          <div className="text-center py-12">
            <div className="text-8xl mb-6">🧠</div>
            <h4 className="text-3xl font-bold text-slate-800 mb-4">Ready to Play?</h4>
            <p className="text-slate-600 mb-8 max-w-md mx-auto">
              Match the couple photos by clicking on the cards. Find all 6 pairs in the fewest moves possible!
            </p>
            <motion.button
              onClick={initializeGame}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-gradient-to-r from-rose-500 to-pink-600 text-white px-8 py-4 rounded-full font-bold text-lg shadow-lg hover:shadow-xl transition-all duration-300"
            >
              Start Game 🎮
            </motion.button>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-3 md:grid-cols-4 gap-4 mb-8">
              {cards.map((card, index) => (
                <CardComponent key={card.id} card={card} index={index} />
              ))}
            </div>

            {/* Game Complete Modal */}
            {gameComplete && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
                >
                  <motion.div
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.8, opacity: 0 }}
                    className="bg-white rounded-3xl p-8 max-w-md mx-4 text-center shadow-2xl"
                  >
                    <div className="text-8xl mb-4">🎉</div>
                    <h4 className="text-3xl font-bold text-slate-800 mb-4">Congratulations!</h4>
                    <p className="text-slate-600 mb-6">
                      You completed the memory game in <span className="font-bold text-rose-600">{moves}</span> moves!
                    </p>
                    {moves === bestScore && (
                      <p className="text-green-600 font-bold mb-4">🏆 New Best Score!</p>
                    )}
                    <div className="flex gap-4">
                      <motion.button
                        onClick={initializeGame}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="flex-1 bg-gradient-to-r from-rose-500 to-pink-600 text-white px-6 py-3 rounded-full font-bold shadow-lg"
                      >
                        Play Again
                      </motion.button>
                      <motion.button
                        onClick={() => setGameStarted(false)}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="flex-1 bg-slate-200 text-slate-700 px-6 py-3 rounded-full font-bold shadow-lg"
                      >
                        Main Menu
                      </motion.button>
                    </div>
                  </motion.div>
                </motion.div>
              )}

            {/* Restart Button */}
            <div className="text-center">
              <motion.button
                onClick={initializeGame}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-slate-200 text-slate-700 px-6 py-3 rounded-full font-bold shadow-lg hover:bg-slate-300 transition-colors"
              >
                Restart Game
              </motion.button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default MemoryGame;
